import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { API_BASE_URL } from '../lib/api';

// Auth timeout to prevent infinite spinner
const AUTH_TIMEOUT_MS = 15000;

// Production domain for cache clearing
const PRODUCTION_DOMAIN = 'sacredsteel.app';

// Session expired state key (for Safari loop prevention)
const SESSION_EXPIRED_KEY = 'ss_session_expired';
const REDIRECT_GUARD_KEY = 'ss_redirect_guard';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const isCheckingAuth = useRef(false);
  const timeoutRef = useRef(null);
  const hasRedirectedOnce = useRef(false);

  // Clear cached auth state if domain mismatch detected
  const clearCachedAuthOnDomainMismatch = useCallback(() => {
    const currentDomain = window.location.hostname;
    const storedDomain = localStorage.getItem('ss_auth_domain');
    
    if (storedDomain && storedDomain !== currentDomain) {
      console.log('[Auth] Domain mismatch detected:', storedDomain, '->', currentDomain);
      console.log('[Auth] Clearing cached auth state...');
      
      // Clear all auth-related storage
      localStorage.removeItem('ss_auth_domain');
      localStorage.removeItem('ss_user');
      localStorage.removeItem(SESSION_EXPIRED_KEY);
      localStorage.removeItem(REDIRECT_GUARD_KEY);
      sessionStorage.clear();
      
      // Clear cookies by setting expired date
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });
      
      return true; // Domain changed
    }
    
    // Store current domain
    localStorage.setItem('ss_auth_domain', currentDomain);
    return false;
  }, []);

  // Check redirect guard to prevent Safari loop
  const checkRedirectGuard = useCallback(() => {
    const guardValue = sessionStorage.getItem(REDIRECT_GUARD_KEY);
    if (guardValue === 'true') {
      console.log('[Auth] Redirect guard active - preventing loop');
      return true;
    }
    return false;
  }, []);

  // Set redirect guard
  const setRedirectGuard = useCallback(() => {
    sessionStorage.setItem(REDIRECT_GUARD_KEY, 'true');
  }, []);

  // Clear redirect guard (call after successful auth)
  const clearRedirectGuard = useCallback(() => {
    sessionStorage.removeItem(REDIRECT_GUARD_KEY);
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    hasRedirectedOnce.current = false;
  }, []);

  const checkAuth = useCallback(async () => {
    console.log('[Auth] Checking session...');
    
    // Skip auth check if we're on the OAuth callback path (hash contains session_id)
    if (window.location.hash?.includes('session_id=')) {
      console.log('[Auth] On OAuth callback path, skipping auth check');
      setLoading(false);
      return null;
    }

    // Prevent concurrent auth checks
    if (isCheckingAuth.current) {
      console.log('[Auth] Auth check already in progress, skipping');
      return user;
    }

    isCheckingAuth.current = true;
    setAuthError(null);

    // Clear timeout from any previous check
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set timeout fallback to prevent infinite spinner
    timeoutRef.current = setTimeout(() => {
      if (loading) {
        console.log('[Auth] Auth check timeout (15s), stopping spinner');
        setLoading(false);
        setInitialized(true);
        setAuthError('Authentication check timed out');
        setSessionExpired(true);
        isCheckingAuth.current = false;
      }
    }, AUTH_TIMEOUT_MS);

    try {
      const response = await api.get('/auth/me', {
        timeout: 10000 // 10s request timeout
      });
      
      console.log('[Auth] Session found:', response.data.email);
      setUser(response.data);
      setAuthError(null);
      setSessionExpired(false);
      clearRedirectGuard(); // Clear guard on successful auth
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      console.log('[Auth] No session found, status:', status || 'network error');
      setUser(null);
      
      // Handle 401 specifically - session expired
      if (status === 401) {
        console.log('[Auth] Session expired (401) - clearing cached user');
        setSessionExpired(true);
        setAuthError('Session expired');
        // Clear any cached user data
        localStorage.removeItem('ss_user');
      } else if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        setAuthError('Network error - cannot reach server');
      } else if (!error.response) {
        setAuthError('Network error');
      }
      
      return null;
    } finally {
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      setLoading(false);
      setInitialized(true);
      isCheckingAuth.current = false;
      console.log('[Auth] Auth check complete, loading=false');
    }
  }, [user, loading, clearRedirectGuard]);

  useEffect(() => {
    // Log startup info
    console.log('[Auth] Initializing auth context...');
    console.log('[Auth] Backend URL:', process.env.REACT_APP_BACKEND_URL || '(using relative URLs)');
    
    // Check for domain mismatch and clear cache if needed
    clearCachedAuthOnDomainMismatch();
    
    // IMPORTANT: Do NOT use cached ss_user as authenticated state
    // Only set user after successful /api/auth/me call
    // Clear any stale cached user on startup
    const cachedUser = localStorage.getItem('ss_user');
    if (cachedUser) {
      console.log('[Auth] Found cached user, will verify with server (NOT treating as authenticated)');
    }
    
    // Run auth check - this is the ONLY way to become authenticated
    checkAuth();
    
    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const login = () => {
    console.log('[Auth] Initiating login...');
    // Redirect to Emergent Auth with dashboard as redirect target
    const redirectUrl = window.location.origin + '/dashboard';
    console.log('[Auth] Redirect URL:', redirectUrl);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    console.log('[Auth] Logging out...');
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('[Auth] Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('ss_user');
      console.log('[Auth] Redirecting to login');
      window.location.href = '/login';
    }
  };

  // Update user state directly (used after successful OAuth)
  const setUserDirectly = (userData) => {
    console.log('[Auth] Setting user directly:', userData?.email);
    setUser(userData);
    setLoading(false);
    setInitialized(true);
    setAuthError(null);
    setSessionExpired(false);
    clearRedirectGuard();
    
    // Cache user for faster subsequent loads
    if (userData) {
      localStorage.setItem('ss_user', JSON.stringify(userData));
    }
  };

  // Refresh user data from backend
  const refreshUser = async () => {
    console.log('[Auth] Refreshing user...');
    try {
      const response = await api.get('/auth/me');
      console.log('[Auth] User refreshed:', response.data.email);
      setUser(response.data);
      setLoading(false);
      setInitialized(true);
      setSessionExpired(false);
      return response.data;
    } catch (error) {
      console.error('[Auth] Refresh user error:', error);
      setUser(null);
      if (error.response?.status === 401) {
        setSessionExpired(true);
      }
      return null;
    }
  };

  const value = {
    user,
    loading,
    initialized,
    authError,
    sessionExpired,
    login,
    logout,
    checkAuth,
    refreshUser,
    setUserDirectly,
    isAuthenticated: !!user,
    checkRedirectGuard,
    setRedirectGuard,
    clearRedirectGuard
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
